import React, { Key, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { CreateEditFillingStationType } from '../../../../Models/fillingStationType'
import fillingStationService from '../../../../Services/fillingStationService'
import Swal from 'sweetalert2'
import state from '../../../../Data/stateNG.json'
import './Fillingstations.scss'

const schema = yup.object().shape({
  businessName: yup.string().required(),
  branchName: yup.string().required(),
  address: yup.string().required(),
  city: yup.string().required(),
  state: yup.string().required(),
  latitude: yup.number().required(),
  longitude: yup.number().required(),
  accountManagerId: yup.string().required(),
  fuelFactsId: yup.string().required(),
  pumps: yup.string().required(),
  openingTimeHour: yup.string().required(),
  closingTimeHour: yup.string().required(),
})

const CreateEditFillingStation = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CreateEditFillingStationType>({
    resolver: yupResolver(schema),
    defaultValues: {
      pumps: '',
    },
  })

  useEffect(() => {
    if (id) {
      setLoading(true)
      fillingStationService
        .getById(id)
        .then((response) => {
          const station = response.data.data
          setValue('businessName', station.businessName)
          setValue('branchName', station.branchName)
          setValue('address', station.address)
          setValue('city', station.city)
          setValue('state', station.state)
          setValue('latitude', station.latitude)
          setValue('longitude', station.longitude)
          setValue('accountManagerId', station.accountManagerId)
          setValue('fuelFactsId', station.fuelFactsId)
          setValue('pumps', station.pumps.join(', '))
          setValue('openingTimeHour', station.openingTimeHour)
          setValue('closingTimeHour', station.closingTimeHour)
        })
        .finally(() => setLoading(false))
    }
  }, [id, setValue])

  const onSubmit = async (data: any) => {
    setLoading(true)
    try {
      const payload = {
        ...data,
        pumps: data.pumps.split(',').map((p: string) => p.trim()),
      }
      if (id) {
        await fillingStationService.update(id, payload)
      } else {
        await fillingStationService.create(payload)
      }
      Swal.fire({
        icon: 'success',
        title: `Filling station ${id ? 'updated' : 'created'} successfully`,
        showConfirmButton: false,
        timer: 1500,
      })
      navigate('/filling-stations')
    } catch (error) {
      console.error('Error saving filling station:', error)
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Something went wrong!',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='filling-station container  mt-4 mb-5 '>
      <div className='card border-0'>
        <div className='card-header border-0'>
          <h5 className='title'>{id ? 'Edit' : 'Create'} Filling Station</h5>
        </div>
        <div className='card-body'>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className='row'>
              <div className='col-md-6'>
                <div className='form-group'>
                  <label>Business Name</label>
                  <input type='text' className='form-control' {...register('businessName')} />
                  <p className='text-danger'>{errors.businessName?.message}</p>
                </div>
              </div>
              <div className='col-md-6'>
                <div className='form-group'>
                  <label>Branch Name</label>
                  <input type='text' className='form-control' {...register('branchName')} />
                  <p className='text-danger'>{errors.branchName?.message}</p>
                </div>
              </div>
            </div>
            <div className='row mt-3'>
              <div className='col-md-12'>
                <div className='form-group'>
                  <label>Address</label>
                  <input type='text' className='form-control' {...register('address')} />
                  <p className='text-danger'>{errors.address?.message}</p>
                </div>
              </div>
            </div>
            <div className='row mt-3'>
              <div className='col-md-6'>
                <div className='form-group'>
                  <label>City</label>
                  <input type='text' className='form-control' {...register('city')} />
                  <p className='text-danger'>{errors.city?.message}</p>
                </div>
              </div>
              <div className='col-md-6'>
                <div className='form-group '>
                  <label htmlFor='state'>State</label>
                  <select
                    id='state'
                    className='form-control'
                    {...register('state', { required: 'field is required' })}
                  >
                    <option value=''>select location</option>
                    {state.map((state: { name: string }, index: Key) => {
                      return <option key={index}>{state.name}</option>
                    })}
                  </select>
                  {errors.state && <div className='error text-danger'>{errors.state.message}</div>}
                </div>
              </div>
            </div>
            <div className='row mt-3'>
              <div className='col-md-6'>
                <div className='form-group'>
                  <label>Latitude</label>
                  <input
                    type='number'
                    step='any'
                    className='form-control'
                    {...register('latitude')}
                  />
                  <p className='text-danger'>{errors.latitude?.message}</p>
                </div>
              </div>
              <div className='col-md-6'>
                <div className='form-group'>
                  <label>Longitude</label>
                  <input
                    type='number'
                    step='any'
                    className='form-control'
                    {...register('longitude')}
                  />
                  <p className='text-danger'>{errors.longitude?.message}</p>
                </div>
              </div>
            </div>
            <div className='row mt-3'>
              <div className='col-md-6'>
                <div className='form-group'>
                  <label>Account Manager ID</label>
                  <input type='text' className='form-control' {...register('accountManagerId')} />
                  <p className='text-danger'>{errors.accountManagerId?.message}</p>
                </div>
              </div>
              <div className='col-md-6'>
                <div className='form-group'>
                  <label>Fuel Facts ID</label>
                  <input type='text' className='form-control' {...register('fuelFactsId')} />
                  <p className='text-danger'>{errors.fuelFactsId?.message}</p>
                </div>
              </div>
            </div>
            <div className='row mt-3'>
              <div className='col-md-12'>
                <div className='form-group'>
                  <label>Pumps</label>
                  <input
                    type='text'
                    className='form-control'
                    {...register('pumps')}
                    placeholder='Enter pumps, separated by commas'
                  />
                  <p className='text-danger'>{errors.pumps?.message}</p>
                </div>
              </div>
            </div>
            <div className='row mt-3'>
              <div className='col-md-6'>
                <div className='form-group'>
                  <label>Opening Time</label>
                  <input type='time' className='form-control' {...register('openingTimeHour')} />
                  <p className='text-danger'>{errors.openingTimeHour?.message}</p>
                </div>
              </div>
              <div className='col-md-6'>
                <div className='form-group'>
                  <label>Closing Time</label>
                  <input type='time' className='form-control' {...register('closingTimeHour')} />
                  <p className='text-danger'>{errors.closingTimeHour?.message}</p>
                </div>
              </div>
            </div>
            <div className='row mt-3'>
              <div className='col-12'>
                <button type='submit' className='btn btn-respond-req' disabled={loading}>
                  {loading ? (
                    <span className='border-spinner border-spinner-sm'></span>
                  ) : id ? (
                    'Update'
                  ) : (
                    'Create'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CreateEditFillingStation
